import {plural, t} from "@lingui/core/macro";

import {calendar} from "@atg-shared/datetime";
import {noTrans} from "@atg-shared/no-trans";
import {GameType} from "@atg-tillsammans-shared/types";

type NumberFormatOptions = {
    delimiter?: string;
    prependUnit?: string;
    appendUnit?: string;
};

/**
 * Formats numbers into a string of spliting the number into blocks of three digits with a given delimiter. Default delimiter is a blank space.
 *
 * Optionaly you can give the number a different delimiter you can choose to have an unit before and/or after the number.
 *
 * e.g. 1337 will become 1 337 and 1000000 will become 1 000 000.
 */
export const formatNumber = (
    num: number,
    options: NumberFormatOptions = {delimiter: " "},
) => {
    const {delimiter, prependUnit, appendUnit} = options;
    let prettyNumber = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, delimiter ?? " ");
    if (prependUnit) prettyNumber = `${prependUnit} ${prettyNumber}`;
    if (appendUnit) prettyNumber = `${prettyNumber} ${appendUnit}`;
    return prettyNumber;
};

export type GameTypeFormatOptions = {
    uppercase?: boolean;
    fullText?: boolean;
};

export const formatGameType = (
    gameType: GameType,
    {uppercase = false, fullText = false}: GameTypeFormatOptions = {},
): string => {
    switch (gameType) {
        case GameType.SP96:
        case GameType.BIG9: {
            const text = t`Big 9`;
            return uppercase ? text.toUpperCase() : text;
        }
        case GameType.DD: {
            const text = fullText ? t`Dagens Dubbel` : gameType;
            return uppercase ? text.toUpperCase() : text;
        }
        case GameType.LD: {
            const text = fullText ? t`Lunch Dubbel` : gameType;
            return uppercase ? text.toUpperCase() : text;
        }
        case GameType.VINNARE:
        case GameType.PLATS:
        case GameType.TVILLING:
        case GameType.KOMB:
        case GameType.TRIO:
        case GameType.TOP7: {
            const text = `${gameType}`.toLowerCase();
            return uppercase
                ? text.toUpperCase()
                : text.charAt(0).toUpperCase() + text.slice(1);
        }
        default:
            return gameType;
    }
};

type GameTypesOptions = {
    lastDelimiter?: string;
};

export const formatGameTypes = (
    gameTypes: GameType[],
    {lastDelimiter}: GameTypesOptions = {lastDelimiter: "&"},
) => {
    const types = gameTypes.map((gameType) => formatGameType(gameType)).join(", ");
    return types.replace(/, ([^,.*]*)$/, ` ${lastDelimiter} $1`);
};

export const formatJackpotAmount = (amount: number) => {
    const number = Math.round(amount / 100000) / 10;
    const jackpotAmount = formatNumber(number < 10 ? number : Math.floor(number), {});
    if (+jackpotAmount < 1) return "";
    return `${jackpotAmount} ${plural(jackpotAmount, {
        one: "Miljon",
        other: "Miljoner",
    })}`;
};

export const formatCapitalized = (value: string): string => {
    const hasDash = value.includes("-");
    const text = value.replace("-", " ").split(" ");
    const newText = text.reduce<string[]>((acc, s) => {
        if (s) {
            return [...acc, s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()];
        }
        return acc;
    }, []);
    return newText.join(hasDash ? "-" : " ");
};

/**
 * TODO: Add market specific rules
 *
 * @param value
 * @returns
 */
export const formatGenitiveCase = (value: string): string => {
    // Swedish rules for genitive case
    if (value.endsWith("s")) return `${value}`;
    return `${value}s`;
};

export const formatGameTypeToSport = (gameType: GameType): string => {
    switch (gameType) {
        case GameType.BIG9:
            return noTrans("Big 9");
        default:
            return noTrans("trav");
    }
};

export const formatTrackName = (
    trackNames: Array<string>,
    options?: {delimiter?: string; lastDelimiter?: string; defaultValue?: string},
): string => {
    const {delimiter = "/", lastDelimiter, defaultValue} = options || {};
    if (trackNames.length < 2) {
        const name = trackNames.join(lastDelimiter ?? delimiter);
        return name === "" ? defaultValue ?? name : name;
    }

    return lastDelimiter
        ? `${trackNames.slice(0, trackNames.length - 1).join(delimiter)}${lastDelimiter}${
              trackNames[trackNames.length - 1]
          }`
        : trackNames.join(delimiter);
};

type FormatRelativeDayOptions = {
    short?: boolean;
    capitalized?: boolean;
    withTime?: boolean;
    noTomorrow?: boolean;
};

export const formatRelativeDay = (
    date: string,
    options: FormatRelativeDayOptions = {
        short: false,
        capitalized: false,
        withTime: false,
        noTomorrow: false,
    },
): string => {
    const timeSuffix = noTrans(options.withTime ? " HH:mm" : "");
    const defaultFormat = noTrans(`${options.short ? "ddd" : "dddd"}${timeSuffix}`);
    const day = calendar(date, {
        lastWeek: defaultFormat,
        lastDay: t`[igår]${timeSuffix}`,
        sameDay: t`[idag]${timeSuffix}`,
        nextDay: options.noTomorrow ? defaultFormat : t`[imorgon]${timeSuffix}`,
        nextWeek: defaultFormat,
        sameElse: defaultFormat,
    });

    return options.capitalized ? day.charAt(0).toUpperCase() + day.slice(1) : day;
};
